import { Link, useSearchParams } from 'react-router-dom';
import { useMsal, } from '@azure/msal-react';
import LoadingAnimation from "../../components/loading/loading";
import { useState, useEffect, useMemo } from "react";
import TenantApi from '../../api/tenant/tenant-api-factory';
import { filterActiveTenants, filterInactiveTenants } from '../../utils/tenantUtils';
import { TenantsContainer } from '../../components/tenants/tenants-container';

import './../properties/property.css';

export const TenantSearch = () => {

    const [searchParams, setSearchParams] = useSearchParams();

    const { instance, inProgress, accounts } = useMsal();

    const api = useMemo(() => new TenantApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const [tenantData, setTenantData] = useState(null);
    const [fetchInProgress, setFetchInProgress] = useState(false);
    const [shouldFetch, setShouldFetch] = useState(true);

    // Derive filters from URL search params (single source of truth)
    const withBalanceParam = searchParams.get('balance');
    const viewParam = searchParams.get('view');
    const statusParam = searchParams.get('status');

    const showWithBalance = withBalanceParam === 'true' || withBalanceParam === '1';
    const displayType = viewParam === 'list' ? 'list' : 'grid';
    const statusFilter = (statusParam === 'active' || statusParam === 'inactive') ? statusParam : 'all';

    useEffect(() => {
        if (shouldFetch && !fetchInProgress) {

            setFetchInProgress(true);

            api.getAll()
                .then(data => {
                    setTenantData(data);
                })
                .catch(error => {
                    console.log("An error occurred getting data from the Tenant Data API.", error);
                    setTenantData(null);
                })
                .finally(() => {
                    setShouldFetch(false);
                    setFetchInProgress(false);
                });
        }
    }, [shouldFetch, fetchInProgress, instance, inProgress, accounts, api]);

    const DisplayToggle = () => (
        <div className="btn-group mb-3">
            <button
                className={`btn btn-sm ${displayType === 'grid' ? 'btn-primary' : 'btn-default'}`}
                onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.delete('view');
                    setSearchParams(params, { replace: true });
                }}
            >
                <i className="fa fa-th-large"></i> Grid
            </button>
            <button
                className={`btn btn-sm ${displayType === 'list' ? 'btn-primary' : 'btn-default'}`}
                onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('view', 'list');
                    setSearchParams(params, { replace: true });
                }}
            >
                <i className="fa fa-list"></i> List
            </button>
        </div>
    );

    return (
        <>
            <ol className="breadcrumb float-xl-end">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/tenant/search">Tenant Search</Link></li>
                <li className="breadcrumb-item active">Results</li>
            </ol>
            <h1 className="page-header">Manage Tenants | <small>manage the Tenants associated with your Company and Landlords</small></h1>
            {/* <div>
                <button type="button" className="btn btn-primary" data-bs-toggle="dropdown">
                    Action&nbsp;&nbsp;<span className="caret"></span>
                </button>
                <div className="dropdown-menu">
                    <div className='dropdown-item link-like-div'><i className='fa fa-plus fa-lg'></i>&nbsp;&nbsp;Add Company</div>
                    <div className='dropdown-item link-like-div'><i className='fa fa-trash fa-lg'></i>&nbsp;&nbsp;{`${showCompanyDelete ? 'Cancel Delete' : 'Delete Companies'}`}</div>
                    {showCompanyDelete &&
                        <div className='dropdown-item link-like-div'><i className='fa fa-trash fa-lg'></i>&nbsp;&nbsp;Delete Selected Company</div>}
                    <div className="dropdown-divider"></div>
                    <div className='dropdown-item link-like-div'><i className='fa fa-cog fa-lg'></i>&nbsp;&nbsp;Something worth dividing</div>
                </div>
            </div> */}

            {!fetchInProgress && tenantData && (
                <>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-3 align-items-center">
                            <div className="form-check form-switch">
                                <label className="form-check-label" htmlFor="withBalance">Tenants with Balance</label>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="withBalance"
                                    checked={showWithBalance}
                                    onChange={e => {
                                        const params = new URLSearchParams(searchParams);
                                        if (e.target.checked) {
                                            params.set('balance', 'true');
                                        } else {
                                            params.delete('balance');
                                        }
                                        setSearchParams(params, { replace: true });
                                    }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label htmlFor="statusFilter" className="form-label mb-0">Show</label>
                                <select
                                    id="statusFilter"
                                    className="form-select form-select-sm"
                                    value={statusFilter}
                                    onChange={e => {
                                        const params = new URLSearchParams(searchParams);
                                        const value = e.target.value;
                                        if (value === 'active' || value === 'inactive') {
                                            params.set('status', value);
                                        } else {
                                            params.delete('status');
                                        }
                                        setSearchParams(params, { replace: true });
                                    }}
                                >
                                    <option value="all">All</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <DisplayToggle />
                    </div>
                    <TenantsContainer
                        tenants={(() => {
                            let list = tenantData ?? [];
                            if (statusFilter === 'active') {
                                list = filterActiveTenants(list);
                            } else if (statusFilter === 'inactive') {
                                list = filterInactiveTenants(list);
                            }
                            if (showWithBalance) {
                                list = list.filter(t => t.balance !== 0);
                            }
                            return list;
                        })()}
                        displayType={displayType}
                    />
                </>
            )}
            {fetchInProgress && <LoadingAnimation />}
            {!fetchInProgress && !tenantData && <>Nothing to show... you may not have permission to view this data</>}
        </>
    );
};